/**
 * The Memory page's Access view, for administrators: the rule in one sentence, the levels board
 * (who stands where, how many items sit at each level), compartments, clearances as a people list,
 * "Check access" for a person, a node or an item, and the guided "Stop someone reading this" flow.
 * Every change saves at once and is audited; there are no drafts here.
 *
 * List groups (Layout.tsx): a level, a compartment and a clearance are each a row with one control
 * or a menu at its end; putting someone at a level, making a compartment and choosing who holds one
 * are sheets.
 */

import { mayRead, SENSITIVITIES, type Entitlement, type MemoryItem, type Sensitivity } from '@aioe/shared';
import { Cpu, Layers, Lock, Persons, Plus, Xmark } from '@gravity-ui/icons';
import { Tooltip } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { Fragment, useState, useSyncExternalStore, type ReactNode } from 'react';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import {
  atLeast,
  clearanceSource,
  compartmentIdFor,
  compartmentName,
  holdsDirectly,
  itemName,
  LEVEL_MEANS,
  LEVEL_TEXT,
  plural,
  reachOf,
  usePeopleReach,
  withClearance,
  type AccessModel,
  type CompartmentRow,
} from './memoryAccessModel.ts';
import { StopReading, type StopTarget } from './stopReading.tsx';
import { SubjectName, SubjectPicker } from './SubjectPicker.tsx';
import { groupsAvailable, subjectName, subscribeNames } from '../directory.ts';
import { listCount, NoMatch, ShowMore, useListSearch } from '../pagedList.tsx';
import { SearchBox } from './ui/SearchBox.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { Explainer } from './ui/Layout.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

const tone = VIEW_TONES.memory!;
const LEVEL_OPTIONS = Object.fromEntries(SENSITIVITIES.map((l) => [l, LEVEL_TEXT[l]])) as Record<Sensitivity, string>;

/** A subject named in a sentence: a person by name, a group as "the X group", a project's members. */
export function Who({ type, id, projects }: { type: string; id: string; projects?: Map<string, string> }) {
  if (type === 'project') return <>members of {projects?.get(id) ?? id}</>;
  if (type === 'group')
    return (
      <>
        the <SubjectName id={id} /> group
      </>
    );
  return <SubjectName id={id} />;
}

/** "Alex, Jo and the Finance group": a list that reads as a sentence. */
export function Names({ items }: { items: ReactNode[] }) {
  return (
    <>
      {items.map((n, i) => (
        <Fragment key={i}>
          {i > 0 ? (i === items.length - 1 ? ' and ' : ', ') : ''}
          {n}
        </Fragment>
      ))}
    </>
  );
}

const errorText = (e: unknown) => problemText(e);

export function AccessView({ model, reload }: { model: AccessModel; reload: () => void }) {
  const [stop, setStop] = useState<StopTarget>();
  const openStop = (t: StopTarget) => setStop(t);
  return (
    <>
      <RuleNote model={model} />
      <LevelsBoard model={model} reload={reload} />
      <CompartmentsSection model={model} reload={reload} onStop={(c) => openStop({ compartment: c.id })} />
      <ClearancesSection model={model} reload={reload} />
      <CheckAccess model={model} reload={reload} onStop={(item, person) => openStop({ item: item.id, person })} />
      {/* A sheet over the view, opened from an item or a compartment. */}
      {stop && <StopReading key={`${stop.item ?? ''}/${stop.compartment ?? ''}/${stop.person ?? ''}`} model={model} target={stop} reload={reload} onClose={() => setStop(undefined)} />}
      <WhereLevelsComeFrom />
    </>
  );
}

/** The rule, and the three words it uses, said once at the top: closed to one line, opened to the definitions. */
function RuleNote({ model }: { model: AccessModel }) {
  return (
    <Explainer id="memory-access" tone={tone} icon={<Layers />} title="How access to memory works" summary="A person reads an item at or below their clearance, when they hold every compartment on it">
      <p>
        <span className="font-medium text-foreground">A person reads an item when its level is at or below their clearance, and they hold every compartment on it.</span> Everyone starts at Internal. Anything no rule labels is Confidential.
      </p>
      <p>
        <span className="font-medium text-foreground">Level:</span> how sensitive an item is: Public, Internal, Confidential or Restricted. <span className="font-medium text-foreground">Clearance:</span> the highest level a person reads. <span className="font-medium text-foreground">Compartment:</span> a need-to-know group, such as a customer or a deal; only those who hold it read what is in it.
      </p>
      {model.hiddenFromMe > 0 ? <p>Counts on this view are of the items you can read yourself; {plural(model.hiddenFromMe, 'more item is', 'more items are')} beyond your own clearance or compartments.</p> : null}
    </Explainer>
  );
}

/* ---------- Levels board ---------- */

function LevelsBoard({ model, reload }: { model: AccessModel; reload: () => void }) {
  const [adding, setAdding] = useState<Sensitivity>();
  const [error, setError] = useState<string>();
  const count = (l: Sensitivity) => model.items.filter((i) => i.label.sensitivity === l).length;
  const standing = (l: Sensitivity) => model.clearances.filter((c) => c.level === l);
  const put = async (s: { type: 'user' | 'group'; id: string }, level: Sensitivity) => {
    setError(undefined);
    try {
      await routes.setClearances(withClearance(model.clearances, s, level));
      setAdding(undefined);
      reload();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <>
      <ListGroup title="Levels" description="Who stands at each level, and how many items sit there. A person reads their level and everything below it.">
        {error && !adding ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
        {[...SENSITIVITIES].reverse().map((l) => {
          const here = standing(l);
          return (
            <ListRow
              key={l}
              title={
                <>
                  {LEVEL_TEXT[l]}
                  <span className="ms-2 text-xs font-normal tabular-nums text-muted">{plural(count(l), 'item')}</span>
                </>
              }
              description={
                <>
                  <span className="block text-foreground">
                    {l === 'internal' ? (
                      <>
                        Everyone else stands here: it is the default.
                        {here.length > 0 && (
                          <>
                            {' '}
                            Also set here: <Names items={here.map((c) => <Who key={c.subjectId} type={c.subjectType} id={c.subjectId} />)} />.
                          </>
                        )}
                      </>
                    ) : here.length ? (
                      <>
                        <Names items={here.map((c) => <Who key={c.subjectId} type={c.subjectType} id={c.subjectId} />)} /> {here.length === 1 && here[0]!.subjectType === 'user' ? 'stands' : 'stand'} here.
                      </>
                    ) : (
                      <span className="text-muted">{l === 'public' ? 'Nobody is held at Public.' : 'Nobody stands here yet.'}</span>
                    )}
                  </span>
                  <span className="block">{LEVEL_MEANS[l]}</span>
                </>
              }
              compactControl
              control={
                <Tooltip delay={300}>
                  <Button size="sm" variant="ghost" isIconOnly aria-label={`Put ${groupsAvailable() ? 'a person or group' : 'a person'} at ${LEVEL_TEXT[l]}`} onPress={() => (setError(undefined), setAdding(l))}>
                    <Plus className="size-4" />
                  </Button>
                  <Tooltip.Content>Put {groupsAvailable() ? 'a person or group' : 'a person'} at {LEVEL_TEXT[l]}</Tooltip.Content>
                </Tooltip>
              }
            />
          );
        })}
      </ListGroup>
      <FormSheet
        isOpen={adding !== undefined}
        width="md"
        title={`Put someone at ${adding ? LEVEL_TEXT[adding] : ''}`}
        description="They read this level and everything below it. A level set for a person wins over their groups’. It saves as soon as you pick."
        onClose={() => setAdding(undefined)}
        footer={
          <Button size="sm" variant="tertiary" onPress={() => setAdding(undefined)}>
            Cancel
          </Button>
        }
      >
        <FormField label={groupsAvailable() ? 'A person or group' : 'A person'} hint={adding ? LEVEL_MEANS[adding] : undefined}>
          <SubjectPicker kinds={['user', 'group']} placeholder={adding ? `Who reads up to ${LEVEL_TEXT[adding]}?` : 'Find a person or group'} onPick={(s) => adding && void put({ type: s.type, id: s.id }, adding)} />
        </FormField>
        <ErrorNote error={error} />
      </FormSheet>
    </>
  );
}

/* ---------- Clearances ---------- */

function ClearancesSection({ model, reload }: { model: AccessModel; reload: () => void }) {
  const [error, setError] = useState<string>();
  // The clearances are the whole list (a save replaces it), so the search and the paging are on the screen: by name, id or level.
  const search = useListSearch(150);
  const [drawn, setDrawn] = useState(CLEARANCES_PAGE);
  const names = useSyncExternalStore(subscribeNames, () => subjectName);
  void names;
  const everyone = [...model.clearances].sort((a, b) => SENSITIVITIES.indexOf(b.level) - SENSITIVITIES.indexOf(a.level));
  const words = search.typed.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const found = words.length ? everyone.filter((c) => words.every((w) => [subjectName(c.subjectId)?.name, subjectName(c.subjectId)?.detail, c.subjectId, LEVEL_TEXT[c.level], c.subjectType === 'group' ? 'group' : 'person'].some((t) => t?.toLowerCase().includes(w)))) : everyone;
  const rows = found.slice(0, drawn);
  const set = async (s: { type: 'user' | 'group'; id: string }, level: Sensitivity | undefined) => {
    setError(undefined);
    try {
      await routes.setClearances(withClearance(model.clearances, s, level));
      reload();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <ListGroup
      title="Clearances"
      count={everyone.length ? listCount(rows.length, { one: 'clearance', many: 'clearances' }, { total: words.length ? undefined : everyone.length, hasMore: found.length > rows.length, searched: words.length > 0 }) : undefined}
      description="Everyone not listed reads up to Internal. A level set for a person wins over their groups’. Changes save at once."
      actions={everyone.length > 6 || words.length ? <SearchBox label="Search clearances" value={search.typed} onChange={(v) => (search.setTyped(v), setDrawn(CLEARANCES_PAGE))} placeholder="Search people and groups" className="w-40 max-w-full @lg:w-56" /> : undefined}
      empty={everyone.length > 0 && found.length === 0 ? <NoMatch tone={tone} icon={<Persons />} q={search.typed.trim()} noun="clearances" onClear={search.clear} /> : everyone.length === 0 ? <Empty tone={tone} icon={<Persons />} title="Everyone reads up to Internal" body={groupsAvailable() ? 'Use the plus on a level above to raise a person or a group.' : 'Use the plus on a level above to raise a person.'} /> : undefined}
    >
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {rows.map((c) => (
        <ListRow
          key={`${c.subjectType}:${c.subjectId}`}
          title={<Who type={c.subjectType} id={c.subjectId} />}
          description={c.subjectType === 'group' ? 'A group: everyone in it reads up to this level' : 'Reads up to'}
          control={
            <>
              <ChoiceSelect label={`Clearance for ${c.subjectId}`} className="w-40" value={c.level} options={LEVEL_OPTIONS} onChange={(v) => void set({ type: c.subjectType, id: c.subjectId }, v)} />
              <RowMenu label={`More for ${c.subjectId}`} items={[{ id: 'reset', label: 'Back to the default, Internal' }]} onAction={() => void set({ type: c.subjectType, id: c.subjectId }, undefined)} />
            </>
          }
        />
      ))}
      {found.length > rows.length ? <ShowMore onPress={() => setDrawn((n) => n + CLEARANCES_PAGE)} /> : null}
    </ListGroup>
  );
}

/** Clearances drawn at a time; Show more draws this many more. */
const CLEARANCES_PAGE = 25;

/* ---------- Compartments ---------- */

function CompartmentsSection({ model, reload, onStop }: { model: AccessModel; reload: () => void; onStop: (c: CompartmentRow) => void }) {
  const [creating, setCreating] = useState(false);
  const [holders, setHolders] = useState<string>();
  const [removing, setRemoving] = useState<CompartmentRow>();
  const [error, setError] = useState<string>();
  const newButton = (
    <Button size="sm" variant="secondary" onPress={() => setCreating(true)}>
      New compartment
    </Button>
  );
  const opened = model.compartments.find((c) => c.id === holders);
  const carrying = (c: CompartmentRow) => model.items.filter((i) => i.label.compartments.includes(c.id));
  return (
    <>
      <ListGroup
        title="Compartments"
        count={model.compartments.length || undefined}
        description="Need-to-know groups. An item in one is read only by those who hold it, whatever their clearance."
        actions={model.compartments.length ? newButton : undefined}
        empty={model.compartments.length === 0 ? <Empty tone={tone} icon={<Lock />} title="No compartments yet" body="Make one for a customer, a deal or anything only some people should read, then hand it to them." action={newButton} /> : undefined}
      >
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
        {model.compartments.map((c) => {
          const people = c.grants.filter((g) => g.subjectType === 'user').length;
          const groups = c.grants.length - people;
          const n = carrying(c).length;
          return (
            <ListRow
              key={c.id}
              title={<span title={c.id}>{c.name}</span>}
              description={
                <>
                  <span className="block">{c.description ?? 'No purpose written down.'}</span>
                  <span className="block text-foreground">
                    {c.grants.length === 0 ? (
                      <span className="text-muted">Held by nobody yet, so nobody reads what is in it.</span>
                    ) : (
                      <>
                        Held by <Names items={c.grants.map((g) => (g.subjectType === 'project' ? <Fragment key={`p${g.subjectId}`}>members of {model.projects.get(g.subjectId) ?? g.subjectId}</Fragment> : <Who key={`${g.subjectType}${g.subjectId}`} type={g.subjectType} id={g.subjectId} />))} />
                      </>
                    )}
                  </span>
                  <span className="block tabular-nums">
                    {plural(n, 'item')} {n === 1 ? 'is' : 'are'} in it{c.grants.length ? ` · ${[people ? plural(people, 'person', 'people') : '', groups ? plural(groups, 'group or project', 'groups or projects') : ''].filter(Boolean).join(', ')}` : ''}
                  </span>
                </>
              }
              compactControl
              control={
                <RowMenu
                  label={`More for ${c.name}`}
                  items={[
                    { id: 'holders', label: 'Who holds it…' },
                    { id: 'stop', label: 'Stop someone reading it…' },
                    { id: 'remove', label: 'Remove the compartment…', danger: true },
                  ]}
                  onAction={(k) => (k === 'holders' ? setHolders(c.id) : k === 'stop' ? onStop(c) : setRemoving(c))}
                />
              }
            />
          );
        })}
      </ListGroup>
      {creating ? <NewCompartment model={model} onDone={() => (setCreating(false), reload())} onCancel={() => setCreating(false)} /> : null}
      {opened ? <Holders model={model} c={opened} reload={reload} onClose={() => setHolders(undefined)} /> : null}
      <ConfirmDialog
        isOpen={removing !== undefined}
        title={`Remove the compartment ${removing?.name ?? ''}?`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          const c = removing;
          setError(undefined);
          try {
            if (c) await routes.deleteCompartment(c.id);
            setRemoving(undefined);
            reload();
          } catch (e) {
            setError(errorText(e));
            setRemoving(undefined);
          }
        }}
      >
        {removing ? (
          <p>
            Everyone who holds it loses it now{removing.grants.length ? ` (${removing.grants.length} ${removing.grants.length === 1 ? 'grant' : 'grants'})` : ''}. The {plural(carrying(removing).length, 'item')} in it keep{carrying(removing).length === 1 ? 's' : ''} the compartment on {carrying(removing).length === 1 ? 'its' : 'their'} label, so nobody reads {carrying(removing).length === 1 ? 'it' : 'them'} until a compartment named <span className="mono">{removing.id}</span> is made again and handed out.
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

function NewCompartment({ model, onDone, onCancel }: { model: AccessModel; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const id = compartmentIdFor(name, model.compartments.map((c) => c.id));
  const create = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.putCompartment({ id, name: name.trim(), ...(purpose.trim() ? { description: purpose.trim() } : {}) });
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet
      width="md"
      title="New compartment"
      description="A need-to-know group. Make it, then choose who holds it from its menu."
      onClose={onCancel}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={busy} isDisabled={!name.trim()} onPress={() => void create()}>
            Create
          </Button>
        </>
      }
    >
      <FormField label="Name" hint={name.trim() ? `Labelling rules and workbenches name it as ${id}.` : undefined}>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme, or Project Orion" autoFocus />
      </FormField>
      <FormField label="What it is for">
        <input className={inputClass} value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="What it is for, in a sentence" />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}

/** Who holds a compartment, in a sheet: add a person, a group or a project's members; take one out. Each change saves at once. */
function Holders({ model, c, reload, onClose }: { model: AccessModel; c: CompartmentRow; reload: () => void; onClose: () => void }) {
  const [dropping, setDropping] = useState<{ subjectType: 'user' | 'group' | 'project'; subjectId: string }>();
  const [error, setError] = useState<string>();
  const carrying = model.items.filter((i) => i.label.compartments.includes(c.id));
  const projectOptions = Object.fromEntries([...model.projects].map(([id, name]) => [id, name]));
  const save = async (grants: CompartmentRow['grants']) => {
    setError(undefined);
    try {
      await routes.setCompartmentGrants(c.id, grants);
      reload();
    } catch (e) {
      setError(errorText(e));
    }
  };
  const add = (g: { subjectType: 'user' | 'group' | 'project'; subjectId: string }) => void save([...c.grants.filter((x) => !(x.subjectType === g.subjectType && x.subjectId === g.subjectId)), g]);
  return (
    <>
      <FormSheet
        width="lg"
        title={`Who holds ${c.name}`}
        description={`${plural(carrying.length, 'item')} ${carrying.length === 1 ? 'is' : 'are'} in it. Those who hold it read them, at their clearance. Changes save at once.`}
        onClose={onClose}
        footer={
          <Button size="sm" variant="tertiary" onPress={onClose}>
            Done
          </Button>
        }
      >
        <FormField label={groupsAvailable() ? 'Add a person or group' : 'Add a person'}>
          <SubjectPicker kinds={['user', 'group']} placeholder={groupsAvailable() ? 'A person or group' : 'A person'} onPick={(s) => add({ subjectType: s.type, subjectId: s.id })} />
        </FormField>
        {model.projects.size > 0 ? (
          <FormField label="Or a project’s members">
            <ChoiceSelect label="Or a project's members" placeholder="Choose a project" className="w-full" value={undefined} options={projectOptions} onChange={(id) => add({ subjectType: 'project', subjectId: id })} />
          </FormField>
        ) : null}
        <ErrorNote error={error} />
        {c.grants.length === 0 ? (
          <p className="text-sm text-muted">Nobody holds it yet, so nobody reads what is in it.</p>
        ) : (
          <ListGroup title="Held by" count={c.grants.length}>
            {c.grants.map((g) => (
              <ListRow
                key={`${g.subjectType}:${g.subjectId}`}
                title={g.subjectType === 'project' ? <>Members of {model.projects.get(g.subjectId) ?? g.subjectId}</> : <Who type={g.subjectType} id={g.subjectId} />}
                description={g.subjectType === 'user' ? 'A person' : g.subjectType === 'group' ? 'A group' : 'A project’s members'}
                compactControl
                control={
                  <Tooltip delay={300}>
                    <Button size="sm" variant="ghost" isIconOnly aria-label={`Take ${g.subjectId} out of ${c.name}`} onPress={() => setDropping(g)}>
                      <Xmark className="size-4" />
                    </Button>
                    <Tooltip.Content>Take out of {c.name}</Tooltip.Content>
                  </Tooltip>
                }
              />
            ))}
          </ListGroup>
        )}
      </FormSheet>
      <ConfirmDialog
        isOpen={dropping !== undefined}
        title={`Take them out of ${c.name}?`}
        confirmLabel="Take out"
        danger
        onCancel={() => setDropping(undefined)}
        onConfirm={async () => {
          await save(c.grants.filter((g) => !(g.subjectType === dropping!.subjectType && g.subjectId === dropping!.subjectId)));
          setDropping(undefined);
        }}
      >
        {dropping && (
          <p>
            {dropping.subjectType === 'project' ? <>Members of {model.projects.get(dropping.subjectId) ?? dropping.subjectId}</> : <Who type={dropping.subjectType} id={dropping.subjectId} />} no longer {dropping.subjectType === 'user' ? 'holds' : 'hold'} {c.name}, and lose{dropping.subjectType === 'user' ? 's' : ''} the {plural(carrying.length, 'item')} in it unless {dropping.subjectType === 'user' ? 'they hold' : 'each holds'} it another way: through a group or a project.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

/* ---------- Check access ---------- */

type CheckMode = 'person' | 'node' | 'item';

/**
 * "Check access": a person, a node or an item, chosen in the group's heading. Each mode is rows of the
 * one group: what to check in the first row, then the answer as rows under it.
 */
function CheckAccess({ model, reload, onStop }: { model: AccessModel; reload: () => void; onStop: (item: MemoryItem, person?: string) => void }) {
  const [mode, setMode] = useState<CheckMode>('person');
  return (
    <ListGroup
      title="Check access"
      description="What someone can read and why, or who can read an item."
      actions={
        <Segment aria-label="Check" size="sm" selectedKey={mode} onSelectionChange={(k) => setMode(String(k) as CheckMode)} className="w-fit">
          <Segment.Item id="person">A person</Segment.Item>
          <Segment.Item id="node">A node</Segment.Item>
          <Segment.Item id="item">An item</Segment.Item>
        </Segment>
      }
    >
      {mode === 'person' && <CheckPerson model={model} reload={reload} />}
      {mode === 'node' && <CheckNode model={model} />}
      {mode === 'item' && <CheckItem model={model} onStop={onStop} />}
    </ListGroup>
  );
}

function CheckPerson({ model, reload }: { model: AccessModel; reload: () => void }) {
  const [who, setWho] = useState<{ id: string; name: string }>();
  const e = useLoad(() => (who ? routes.orgEntitlement(who.id) : Promise.resolve(undefined)), [who?.id, model.clearances, model.compartments]);
  const [error, setError] = useState<string>();
  return (
    <>
      <ListRow title="Person" description={who ? `Checking ${who.name}` : 'Who to check'} control={<SubjectPicker className="w-72 max-w-full" kinds={['user']} placeholder="Find a person" onPick={(p) => setWho({ id: p.id, name: p.name })} />} />
      {e.error || error ? (
        <ListBlock>
          <ErrorNote error={e.error ?? error} />
        </ListBlock>
      ) : null}
      {who && e.data && (
        <PersonReach
          model={model}
          userId={who.id}
          e={e.data}
          onRaise={async (level) => {
            setError(undefined);
            try {
              await routes.setClearances(withClearance(model.clearances, { type: 'user', id: who.id }, level));
              reload();
            } catch (err) {
              setError(errorText(err));
            }
          }}
        />
      )}
    </>
  );
}

/**
 * What one person reads, and why the rest is hidden from them, in words: rows for a list group (their
 * clearance and compartments, what they read of the organisation's memory, and raising them when that
 * would let them read what is hidden).
 */
export function PersonReach({ model, userId, e, onRaise }: { model: AccessModel; userId: string; e: Entitlement; onRaise?: (level: Sensitivity) => Promise<void> }) {
  const { reads, above, missing } = reachOf(e, model.items);
  const source = clearanceSource(model, userId, e);
  const highestHidden = SENSITIVITIES.filter((l) => above.has(l)).at(-1);
  const raiseTo = highestHidden && model.me && atLeast(model.me.clearance, highestHidden) ? highestHidden : undefined;
  const hidden = [...above].reduce((n, [, c]) => n + c, 0) + [...missing].reduce((n, [, c]) => n + c, 0);
  return (
    <>
      <ListRow
        title={
          <>
            <SubjectName id={userId} /> reads up to {LEVEL_TEXT[e.clearance]}
          </>
        }
        description={
          <>
            {source === 'own' ? 'Set for them' : source === 'group' ? 'From one of their groups' : 'The default'}
            {', and '}
            {e.compartments.length ? (
              <>
                holds <Names items={e.compartments.map((c) => <Fragment key={c}>{compartmentName(model, c)}</Fragment>)} />
                {e.compartments.some((c) => !holdsDirectly(model, userId, c)) ? ' (some through a group or a project)' : ''}.
              </>
            ) : (
              'holds no compartments.'
            )}
          </>
        }
      />
      <ListRow
        title={`Reads ${reads.length.toLocaleString()} of ${plural(model.items.length, 'item')}`}
        description={
          hidden ? (
            <>
              {[...above].map(([l, n], i) => (
                <Fragment key={l}>
                  {i ? ' ' : ''}
                  {n.toLocaleString()} {n === 1 ? 'is' : 'are'} hidden because {n === 1 ? 'it is' : 'they are'} {LEVEL_TEXT[l]}, above their clearance.
                </Fragment>
              ))}
              {[...missing].map(([c, n], i) => (
                <Fragment key={c}>
                  {i || above.size ? ' ' : ''}
                  {n.toLocaleString()} {n === 1 ? 'is' : 'are'} in {compartmentName(model, c)}, which they do not hold.
                </Fragment>
              ))}
            </>
          ) : (
            'Nothing in the organisation’s memory is hidden from them.'
          )
        }
        control={
          raiseTo && onRaise ? (
            <Button size="sm" variant="secondary" onPress={() => void onRaise(raiseTo)}>
              Raise them to {LEVEL_TEXT[raiseTo]}
            </Button>
          ) : undefined
        }
      />
    </>
  );
}

function CheckNode({ model }: { model: AccessModel }) {
  const nodes = useLoad(() => routes.secretNodes().catch(() => undefined), []);
  const [node, setNode] = useState<string>();
  const list = nodes.data?.nodes ?? [];
  if (nodes.loading && !nodes.data) return <ListBlock className="text-sm text-muted">Reading the nodes…</ListBlock>;
  if (list.length === 0)
    return (
      <ListBlock>
        <Empty compact tone={tone} icon={<Cpu />} title="No nodes yet" body="Once a node joins and is approved, check here whose memory it reads." />
      </ListBlock>
    );
  return (
    <>
      <ListRow title="Node" description="Which node to check" control={<ChoiceSelect label="Node" placeholder="Choose a node" className="w-72 max-w-full" value={node} options={Object.fromEntries(list.map((n) => [n.deviceId, n.name ?? n.hostname ?? n.deviceId]))} onChange={setNode} />} />
      {node && <NodeMemoryReach nodeId={node} model={model} />}
    </>
  );
}

/**
 * Which people a node reads project memory as: the ones who let it run their work (or an
 * administrator assigned). For anyone else it names, it reads at Internal with no compartments.
 * Rows for a list group, shown on Check access and on the node's own page in the same words; with
 * `editable`, each person can be stopped and a person added (in a sheet).
 */
export function NodeMemoryReach({ nodeId, model, editable }: { nodeId: string; model?: AccessModel; editable?: boolean }) {
  const data = useLoad(() => routes.secretNodes().catch(() => undefined), [nodeId]);
  const delegates = (data.data?.delegates ?? []).filter((d) => d.nodeId === nodeId);
  const reach = useLoad(async () => {
    const out = new Map<string, Entitlement>();
    await Promise.all(delegates.map((d) => routes.orgEntitlement(d.principal).then((e) => void out.set(d.principal, e)).catch(() => undefined)));
    return out;
  }, [delegates.map((d) => d.principal).join(',')]);
  // Compartment names: from the Access view's model, or loaded here on a node's page.
  const names = useLoad(() => (model ? Promise.resolve(undefined) : routes.compartments().then((cs) => new Map(cs.map((c) => [c.id, c.name]))).catch(() => undefined)), [Boolean(model)]);
  const nameOf = (id: string) => (model ? compartmentName(model, id) : (names.data?.get(id) ?? id));
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string>();
  const change = async (principal: string, on: boolean) => {
    setError(undefined);
    try {
      await routes.letNodeRunMyWork(nodeId, principal, on);
      setAdding(false);
      data.reload();
    } catch (e) {
      setError(errorText(e));
    }
  };
  if (!data.data)
    return data.error ? (
      <ListBlock>
        <ErrorNote error={data.error} />
      </ListBlock>
    ) : (
      <ListBlock className="text-sm text-muted">Reading whose work it runs…</ListBlock>
    );
  return (
    <>
      {delegates.length === 0 ? <ListBlock className="text-sm">Nobody has let it run their work, so it reads project memory at Internal with no compartments, whoever it names.</ListBlock> : null}
      {delegates.map((d) => {
        const e = reach.data?.get(d.principal);
        return (
          <ListRow
            key={d.principal}
            title={<SubjectName id={d.principal} />}
            description={e ? `Reads up to ${LEVEL_TEXT[e.clearance]}${e.compartments.length ? `, holds ${e.compartments.map(nameOf).join(', ')}` : ', no compartments'}` : undefined}
            status={<StatusMark state={d.how === 'consent' ? 'connected' : 'online'} label={d.how === 'consent' ? 'They agreed' : 'An administrator assigned it'} />}
            compactControl
            control={
              editable ? (
                <Tooltip delay={300}>
                  <Button size="sm" variant="ghost" isIconOnly aria-label="Stop reading as them" onPress={() => void change(d.principal, false)}>
                    <Xmark className="size-4" />
                  </Button>
                  <Tooltip.Content>Stop reading as them</Tooltip.Content>
                </Tooltip>
              ) : undefined
            }
          />
        );
      })}
      {editable ? (
        <ListRow
          title="Let it run someone’s work"
          description={delegates.length ? 'For anyone else it names, it reads at Internal with no compartments.' : 'It then reads project memory as that person while it runs their work.'}
          control={
            <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>
              Add a person
            </Button>
          }
        />
      ) : delegates.length ? (
        <ListBlock className="text-xs text-muted">For anyone else it names, it reads at Internal with no compartments.</ListBlock>
      ) : null}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {editable ? (
        <FormSheet isOpen={adding} width="md" title="Let it run someone’s work" description="While it runs their work, it reads project memory as they would: up to their clearance, in the compartments they hold." onClose={() => setAdding(false)}>
          <FormField label="Whose work may it run?">
            <SubjectPicker kinds={['user']} placeholder="Find a person" onPick={(s) => void change(s.id, true)} />
          </FormField>
          <ErrorNote error={error} />
        </FormSheet>
      ) : null}
    </>
  );
}

function CheckItem({ model, onStop }: { model: AccessModel; onStop: (item: MemoryItem, person?: string) => void }) {
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<string>();
  const f = filter.trim().toLowerCase();
  const matches = f ? model.items.filter((i) => itemName(i).toLowerCase().includes(f)).slice(0, 8) : [];
  const item = model.items.find((i) => i.id === picked);
  if (model.items.length === 0) return <ListBlock className="text-sm text-muted">The organisation’s memory has nothing you can read yet.</ListBlock>;
  return (
    <>
      <ListRow
        title="Item"
        htmlFor="check-item"
        description={item ? `Checking ${itemName(item)}` : 'Find it by name'}
        control={<input id="check-item" className={`${inputClass} w-72 max-w-full`} value={filter} onChange={(e) => (setFilter(e.target.value), setPicked(undefined))} placeholder="Find an item by name" />}
      />
      {!item && matches.map((i) => <ListRow key={i.id} title={itemName(i)} description={LEVEL_TEXT[i.label.sensitivity]} onPress={() => setPicked(i.id)} />)}
      {!item && f && matches.length === 0 ? <ListBlock className="text-sm text-muted">Nothing you can read is called that.</ListBlock> : null}
      {item && <WhoCanRead model={model} item={item} onStop={(person) => onStop(item, person)} />}
    </>
  );
}

/** Who reads one item, as rows for a list group: its level and compartments, then the readers it lets in, with the way to stop one. */
export function WhoCanRead({ model, item, onStop }: { model: AccessModel; item: MemoryItem; onStop?: (person?: string) => void }) {
  const people = usePeopleReach(model);
  const l = item.label;
  const readers = [...(people.data ?? [])].filter(([, e]) => mayRead(e, l)).map(([id]) => id);
  const groupReaders = model.clearances.filter((c) => c.subjectType === 'group' && atLeast(c.level, l.sensitivity) && l.compartments.every((x) => model.compartments.find((k) => k.id === x)?.grants.some((g) => g.subjectType === 'group' && g.subjectId === c.subjectId)));
  const projectHolders = l.compartments.length ? [...model.projects.keys()].filter((p) => l.compartments.every((x) => model.compartments.find((k) => k.id === x)?.grants.some((g) => g.subjectType === 'project' && g.subjectId === p))) : [];
  const everyone = !l.compartments.length && atLeast('internal', l.sensitivity);
  const lowered = model.clearances.filter((c) => c.level === 'public');
  return (
    <>
      <ListRow
        title={itemName(item)}
        description={
          <>
            {LEVEL_TEXT[l.sensitivity]}
            {l.compartments.length ? (
              <>
                , in <Names items={l.compartments.map((c) => <Fragment key={c}>{compartmentName(model, c)}</Fragment>)} />
              </>
            ) : null}
          </>
        }
      />
      <ListRow
        title="Who reads it"
        description={
          everyone ? (
            <>
              Everyone in the organisation
              {l.sensitivity === 'internal' && lowered.length ? (
                <>
                  , except <Names items={lowered.map((c) => <Who key={c.subjectId} type={c.subjectType} id={c.subjectId} />)} />, held at Public
                </>
              ) : null}
              .
            </>
          ) : (
            <>
              Everyone cleared to {LEVEL_TEXT[l.sensitivity]} or above{l.compartments.length ? ` who also holds ${l.compartments.map((c) => compartmentName(model, c)).join(' and ')}` : ''}.{' '}
              {readers.length || groupReaders.length || projectHolders.length ? (
                <>
                  That is{' '}
                  <Names
                    items={[
                      ...readers.map((id) => <SubjectName key={id} id={id} />),
                      ...groupReaders.map((c) => <Who key={`g${c.subjectId}`} type="group" id={c.subjectId} />),
                      ...projectHolders.map((p) => <Fragment key={`p${p}`}>cleared members of {model.projects.get(p) ?? p}</Fragment>),
                    ]}
                  />
                  .
                </>
              ) : people.loading ? null : (
                'Nobody named in clearances or compartments reads it.'
              )}
            </>
          )
        }
        control={
          onStop ? (
            <Button size="sm" variant="secondary" onPress={() => onStop(readers.length === 1 ? readers[0] : undefined)}>
              Stop someone reading this
            </Button>
          ) : undefined
        }
      />
    </>
  );
}

/* ---------- Where levels come from ---------- */

function WhereLevelsComeFrom() {
  return (
    <ListGroup title="Where levels come from" description="How an item gets its level and compartments.">
      <ListRow title="Anything no rule labels is Confidential" description="So a new project’s memory is hidden from people at Internal until a rule labels the safe parts lower." />
      <ListRow to="/projects" title="Each project’s labelling rules" description="Set the level and compartments by folder or source, on the project’s Memory tab." />
      <ListRow to="/settings/integrations" title="Microsoft Purview labels" description="Map to these levels when Purview is connected." />
      <ListRow title="What agents write back" description="Is labelled at the highest level their session read, and keeps its compartments." />
    </ListGroup>
  );
}
